import { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Card, EmptyState, FilterBar, GroupByDropdown, ViewSwitcher, CalendarNavigation, ItemTile, GroupedItemList } from '@/components/ui';
import { WeeklyView, MonthlyView } from '@/components/calendar';
import { useItemStore, useUIStore, useSettingsStore } from '@/stores';
import { isRecurringItem } from '@perfin/shared';
import type { Item } from '@perfin/shared';
import type { SearchTabKey } from '@/stores/uiStore';

// Search tabs
const searchTabs: { key: SearchTabKey; label: string }[] = [
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'recurring', label: 'Recurring' },
];

// Helper to get link path for item
function getItemLinkPath(item: Item): string {
  const basePaths: Record<string, string> = {
    task: '/tasks',
    transaction: '/transactions',
    note: '/notes',
    keyDate: '/dates',
  };
  const basePath = basePaths[item.type] || '/items';
  return `${basePath}/${item.id}`;
}

export function SearchResultsPage() {
  const navigate = useNavigate();
  const items = useItemStore((state) => state.items);
  const { searchQuery, setSearchQuery, viewMode, calendarOffset, groupBy, categoryFilters, pageFilters, searchTab, setSearchTab, setOwnerFilter } = useUIStore();
  const { currentUserId, members } = useSettingsStore();
  const isAdmin = useSettingsStore((state) => state.currentUserRole === 'admin' || state.currentUserRole === 'superAdmin');

  const currentViewMode = viewMode?.search ?? 'card';
  const currentGroupBy = groupBy?.search ?? 'date';
  const catFilter = categoryFilters?.search ?? { categoryId: null, subCategoryId: null };
  const pFilters = pageFilters?.search ?? { ownerFilter: null, statusFilter: null, priorityFilter: null };

  // Bug #5: Validate owner filter - if references non-existent user, reset to current user
  useEffect(() => {
    if (pFilters.ownerFilter && members.length > 0) {
      const ownerExists = members.some(m => m.userId === pFilters.ownerFilter);
      if (!ownerExists && currentUserId) {
        setOwnerFilter('search', currentUserId);
      }
    }
  }, [pFilters.ownerFilter, members, currentUserId, setOwnerFilter]);

  // Redirect if no search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      navigate('/');
    }
  }, [searchQuery, navigate]);

  // Search and filter items
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase().trim();

    // Search across all item fields (excluding IDs like category/subCategory)
    let results = items.filter((item) => {
      const searchableFields = [
        item.name,
        item.description,
        (item as unknown as { content?: string }).content, // Note content
        (item as unknown as { tags?: string[] }).tags?.join(' '), // Note tags
      ].filter(Boolean);

      return searchableFields.some((field) =>
        field?.toLowerCase().includes(query)
      );
    });

    // Filter by search tab (scheduled = non-recurring, recurring = recurring items)
    if (searchTab === 'scheduled') {
      results = results.filter((item) => !isRecurringItem(item));
    } else if (searchTab === 'recurring') {
      results = results.filter((item) => isRecurringItem(item));
    }

    // Apply category filter
    if (catFilter.categoryId) {
      results = results.filter((item) => item.category === catFilter.categoryId);
    }
    if (catFilter.subCategoryId) {
      results = results.filter((item) => item.subCategory === catFilter.subCategoryId);
    }

    // Apply status filter (only for scheduled tab - recurring items have per-occurrence status)
    if (searchTab === 'scheduled' && pFilters.statusFilter) {
      results = results.filter((item) => {
        const status = (item as unknown as { status?: string }).status;
        return status === pFilters.statusFilter;
      });
    }

    // Apply priority filter
    if (pFilters.priorityFilter) {
      results = results.filter((item) => item.priority === pFilters.priorityFilter);
    }

    // Owner filtering
    if (pFilters?.ownerFilter) {
      results = results.filter((item) => item.owner === pFilters.ownerFilter);
    } else if (!isAdmin && currentUserId) {
      // Standard users without owner filter: show items they own OR created
      results = results.filter((item) => item.owner === currentUserId || item.createdBy === currentUserId);
    }

    return results;
  }, [items, searchQuery, searchTab, catFilter, pFilters, currentUserId, isAdmin]);

  // Group count by type for stats
  const typeCounts = useMemo(() => {
    const counts = { task: 0, transaction: 0, note: 0, keyDate: 0 };
    filteredItems.forEach((item) => {
      counts[item.type]++;
    });
    return counts;
  }, [filteredItems]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Search Results</h1>
          <p className="text-gray-600 mt-1">
            {filteredItems.length} result{filteredItems.length !== 1 ? 's' : ''} for "{searchQuery}"
            {filteredItems.length > 0 && (
              <span className="text-gray-400 ml-2">
                ({typeCounts.task} tasks, {typeCounts.transaction} transactions, {typeCounts.note} notes, {typeCounts.keyDate} key dates)
              </span>
            )}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <FilterBar itemType="task" pageKey="search" hideStatusFilter={searchTab === 'recurring'} />
        <hr className="w-full border-gray-300 md:hidden" />
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          {currentViewMode === 'card' && <GroupByDropdown pageKey="search" showInfoTooltip excludeOptions={searchTab === 'recurring' ? ['date'] : []} />}
          <ViewSwitcher pageKey="search" />
        </div>
      </div>

      {/* Search Tabs */}
      <div className="flex items-center justify-between border-b border-gray-200">
        <div className="flex gap-1">
          {searchTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setSearchTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                searchTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        {currentViewMode !== 'card' && (
          <CalendarNavigation pageKey="search" mode={currentViewMode} />
        )}
      </div>

      {/* Card View */}
      {currentViewMode === 'card' && (
        filteredItems.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Search className="h-12 w-12" />}
              title="No results found"
              description={`No items match "${searchQuery}". Try a different search term.`}
              action={{
                label: 'Clear Search',
                onClick: () => {
                  setSearchQuery('');
                  navigate('/');
                },
              }}
            />
          </Card>
        ) : (
          <GroupedItemList
            items={filteredItems}
            groupBy={currentGroupBy}
            renderGroup={(key, items) => (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {key} ({items.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {items.map((item) => (
                    <ItemTile key={item.id} item={item} linkPath={getItemLinkPath(item)} hideStatusToggle={searchTab === 'recurring'} />
                  ))}
                </div>
              </div>
            )}
            renderItem={(item: Item) => <ItemTile key={item.id} item={item} linkPath={getItemLinkPath(item)} hideStatusToggle={searchTab === 'recurring'} />}
          />
        )
      )}

      {/* Weekly View */}
      {currentViewMode === 'weekly' && (
        <WeeklyView
          items={filteredItems}
          offset={calendarOffset?.search?.weekly ?? 0}
          showItemType={true}
        />
      )}

      {/* Monthly View */}
      {currentViewMode === 'monthly' && (
        <MonthlyView
          items={filteredItems}
          offset={calendarOffset?.search?.monthly ?? 0}
          showItemType={true}
        />
      )}
    </div>
  );
}
